// components/EditLugarModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaImage, FaExclamationTriangle, FaMapMarkerAlt } from 'react-icons/fa';
import LocationSelector from '../LocationSelector';

const EditLugarModal = ({ lugarToEdit, setLugarToEdit, handleEditSubmit, isLoading, onClose }) => {
  const [previewImage, setPreviewImage] = useState('');
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  
  // Iniciar la vista previa de la imagen cuando se abre el modal
  useEffect(() => {
    if (lugarToEdit?.imagen) {
      setPreviewImage(lugarToEdit.imagen);
    }
  }, [lugarToEdit]);
  
  // Manejar cambios en la URL de la imagen
  useEffect(() => {
    if (lugarToEdit?.imagen) {
      setImageLoading(true);
      setImageError(false);
      
      const img = new Image();
      img.onload = () => {
        setPreviewImage(lugarToEdit.imagen);
        setImageLoading(false);
        setImageError(false);
      };
      img.onerror = () => {
        setImageError(true);
        setImageLoading(false);
      };
      img.src = lugarToEdit.imagen;
    }
  }, [lugarToEdit?.imagen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setLugarToEdit(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setLugarToEdit(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // ✅ Handlers para el LocationSelector con useCallback
  const handleRegionChange = useCallback((regionName) => {
    setLugarToEdit(prev => ({
      ...prev,
      region: regionName,
      ciudad: '' // Limpiar ciudad cuando cambia la región
    }));
  }, [setLugarToEdit]);

  const handleCommuneChange = useCallback((communeName) => {
    setLugarToEdit(prev => ({
      ...prev,
      ciudad: communeName
    }));
  }, [setLugarToEdit]);

  const testImageUrl = () => {
    if (lugarToEdit.imagen) {
      setImageLoading(true);
      setImageError(false);
      
      const img = new Image();
      img.onload = () => {
        setPreviewImage(lugarToEdit.imagen);
        setImageLoading(false);
        setImageError(false);
      };
      img.onerror = () => {
        setImageError(true);
        setImageLoading(false);
      };
      img.src = lugarToEdit.imagen;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto backdrop-blur-sm">
      <div className="bg-zinc-800 rounded-xl shadow-2xl max-w-4xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto border border-zinc-700">
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-2xl font-bold text-white">Editar Lugar de Evento</h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-white hover:bg-zinc-700 p-2 rounded-full transition-all duration-200"
            aria-label="Cerrar"
          >
            <FaTimes />
          </button>
        </div>
        
        <form onSubmit={(e) => handleEditSubmit(e, lugarToEdit)} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Nombre del Lugar *
              </label>
              <input
                type="text"
                name="nombre"
                value={lugarToEdit.nombre || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                required
                placeholder="Nombre del lugar"
              />
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                URL de Imagen *
              </label>
              <div className="flex gap-4 items-start">
                <div className="flex-grow">
                  <div className="flex gap-2">
                    <input
                      type="url"
                      name="imagen"
                      value={lugarToEdit.imagen || ''}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-zinc-700/50 border ${imageError ? 'border-red-500' : 'border-zinc-600'} rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200`}
                      required
                      placeholder="https://ejemplo.com/imagen.jpg"
                    />
                    <button
                      type="button"
                      onClick={testImageUrl}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                    >
                      Probar
                    </button>
                  </div>
                  
                  {imageError && (
                    <p className="text-red-400 text-xs mt-1 flex items-center">
                      <FaExclamationTriangle className="mr-1" /> 
                      No se pudo cargar la imagen. Verifica la URL o prueba con otra imagen.
                    </p>
                  )}
                </div>
                
                {/* Vista previa de la imagen */}
                <div className="w-28 h-28 bg-zinc-700/30 border border-zinc-600 rounded-lg overflow-hidden flex items-center justify-center relative">
                  {imageLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-zinc-800/70">
                      <svg className="animate-spin h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  ) : previewImage && !imageError ? (
                    <img 
                      src={previewImage} 
                      alt="Vista previa" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-zinc-500">
                      <FaImage className="text-3xl mb-1" />
                      <span className="text-xs">Sin imagen</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Dirección *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                name="direccion"
                value={lugarToEdit.direccion || ''}
                onChange={handleChange}
                className="pl-10 w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                required
                placeholder="Dirección completa del lugar"
              />
            </div>
          </div>

          {/* ✅ Reemplazar inputs individuales con LocationSelector */}
          <div className="space-y-2">
            <LocationSelector
              selectedRegion={lugarToEdit.region}
              selectedCommune={lugarToEdit.ciudad}
              onRegionChange={handleRegionChange}
              onCommuneChange={handleCommuneChange}
              disabled={isLoading}
            />
          </div>

  {/* ✅ Resumen de ubicación seleccionada */}
          {lugarToEdit.region && lugarToEdit.ciudad && lugarToEdit.direccion && (
            <div className="bg-zinc-700/30 border border-zinc-600 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-300 mb-2">Ubicación seleccionada:</h4>
              <p className="text-white">
                <FaMapMarkerAlt className="inline mr-2 text-red-500" />
                {lugarToEdit.direccion}, {lugarToEdit.ciudad}, {lugarToEdit.region}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Descripción *
            </label>
            <textarea
              name="descripcion"
              value={lugarToEdit.descripcion || ''}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 resize-none"
              required
              placeholder="Descripción del lugar, capacidad, características especiales..."
            ></textarea>
          </div>


          
          {/* Checkbox para habilitar/deshabilitar */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="habilitado"
              name="habilitado"
              checked={lugarToEdit.habilitado || false}
              onChange={handleChange}
              className="h-4 w-4 text-red-600 focus:ring-red-500 rounded border-zinc-500 bg-zinc-700"
            />
            <label htmlFor="habilitado" className="text-sm font-medium text-gray-300">
              Lugar activo
            </label>
          </div>
          
          <div className="flex justify-end space-x-4 pt-4 border-t border-zinc-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-all duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading || imageError || !lugarToEdit.region || !lugarToEdit.ciudad}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center min-w-[140px] transition-all duration-200"
              title={
                imageError ? 'Corrige la URL de la imagen para continuar' : 
                !lugarToEdit.region || !lugarToEdit.ciudad ? 'Selecciona región y comuna para continuar' : ''
              }
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Guardando...
                </>
              ) : (
                'Guardar Cambios'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditLugarModal;